import { cn } from "@/lib/utils";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";
import * as React from "react";
import { Logo } from "../Logo";
import {
  MultiStepFormStepper,
  type StepperStep,
} from "../MultiStepFormStepper";
import { Button } from "./button";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn("fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm", className)}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  hideCloseButton,
  hideLogo,
  stepperSteps,
  onClose,
  closeDisabled,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  hideCloseButton?: boolean;
  hideLogo?: boolean;
  stepperSteps?: StepperStep[];
  onClose?: () => void;
  /** The close button shows but cannot be used (a write is running). */
  closeDisabled?: boolean;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "bg-background fixed top-[50%] left-[50%] z-50 w-full translate-x-[-50%] translate-y-[-50%] gap-4 sm:rounded-xl sm:border sm:border-border sm:shadow-depth-lg duration-200 sm:max-w-[90%] overflow-hidden",
          "h-[100dvh] sm:h-[90%] max-h-[100dvh] sm:max-h-[calc(100dvh-3rem)] overflow-y-auto flex flex-col",
          className,
        )}
        {...props}
      >
        {(!hideLogo || !hideCloseButton || !!stepperSteps?.length) && (
          <div className="flex shrink-0 items-center justify-between min-h-16 w-full relative border-b border-border bg-background px-5 py-3">
            {!hideLogo && <Logo variant="It" className="w-auto h-6" />}
            {stepperSteps && stepperSteps.length > 0 && (
              <div className="absolute top-0 left-0 right-0 flex justify-center pointer-events-none">
                <div
                  data-no-close-button={hideCloseButton}
                  className="px-12 sm:px-16 w-full max-w-full min-h-16 flex items-center justify-center"
                >
                  <MultiStepFormStepper steps={stepperSteps} />
                </div>
              </div>
            )}
            {!hideCloseButton && (
              <DialogPrimitive.Close asChild>
                <button
                  type="button"
                  disabled={closeDisabled}
                  data-alone={hideLogo || !stepperSteps?.length}
                  className="cursor-pointer ring-offset-background focus:ring-ring opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none disabled:opacity-30 [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5 size-9 flex items-center justify-center rounded-md hover:bg-muted data-[alone=true]:ms-auto relative z-10"
                  onClick={onClose}
                >
                  <XIcon />
                  <span className="sr-only">Close</span>
                </button>
              </DialogPrimitive.Close>
            )}
          </div>
        )}
        <div
          data-has-stepper={!!stepperSteps?.length}
          className="flex-1 w-full min-h-0 flex flex-col data-[has-stepper=true]:sm:mt-2"
        >
          {children}
        </div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogMain({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      className={cn("flex-1 min-h-0 overflow-y-auto flex flex-col", className)}
      {...props}
    />
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "text-left flex flex-col items-start gap-3 px-6",
        className,
      )}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "w-full shrink-0 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-background px-5 sm:px-8 border-t border-border flex items-center justify-between gap-3",
        className,
      )}
      {...props}
    />
  );
}

function DialogBackButton({
  className,
  ...props
}: Omit<React.ComponentProps<"button">, "children">) {
  return (
    <Button
      variant="link"
      type="button"
      className={cn(
        "w-fit px-2 -ms-2 no-underline hover:no-underline justify-start",
        className,
      )}
      {...props}
    >
      &lt; Back
    </Button>
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-2xl font-medium tracking-[-0.025em]", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-sm text-muted-foreground leading-relaxed", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBackButton,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
